import { useMemo } from 'react'
import type { ChartData, ChartOptions, ScriptableContext } from 'chart.js'
import { Line } from 'react-chartjs-2'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import type { IPlatformDashboardGrowthTrend } from '@brushfire/types'
import '@/features/campaigns/charts/registerCharts'
import { PLATFORM_CHART_CARD } from '../lib/platform-dashboard-layout'
import {
  formatGrowthTrendTooltipTitle,
  growthTrendXAxisTitle,
} from '../lib/growth-trend-chart'
import { WeekMonthToggle } from './WeekMonthToggle'

interface Props {
  trend: IPlatformDashboardGrowthTrend
  growthPeriod: 'week' | 'month'
  onGrowthPeriodChange: (value: 'week' | 'month') => void
  isPeriodRefreshing?: boolean
}

const { growthTrend: card, line: lineColors, ekyc: ekycLegend } = PLATFORM_CHART_CARD

const GROWTH_TREND_LEGEND = [
  { key: 'sensemakers', label: 'Sensemakers', color: lineColors.sensemakers },
  { key: 'signalProviders', label: 'Signal Providers', color: lineColors.signalProviders },
] as const

const tickFont = {
  family: 'Inter, sans-serif',
  size: card.tickFontSize,
  weight: 'bold' as const,
}

const axisTitleFont = {
  family: card.axisTitleFontFamily,
  size: card.axisTitleFontSize,
  weight: 'bold' as const,
}

/** Figma 37:1281 — gradient fill fading under the primary line */
function sensemakersAreaFill(context: ScriptableContext<'line'>): string | CanvasGradient {
  const { chart } = context
  const { ctx, chartArea } = chart
  if (!chartArea) {
    return lineColors.sensemakersFill
  }
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
  gradient.addColorStop(0, 'rgba(0, 140, 208, 0.28)')
  gradient.addColorStop(1, 'rgba(0, 140, 208, 0.02)')
  return gradient
}

export function GrowthTrendChart({
  trend,
  growthPeriod,
  onGrowthPeriodChange,
  isPeriodRefreshing = false,
}: Props) {
  const labels = trend.sensemakers.map((b) => b.label)

  const data = useMemo<ChartData<'line'>>(
    () => ({
      labels,
      datasets: [
        {
          type: 'line' as const,
          label: 'Sensemakers',
          data: trend.sensemakers.map((b) => b.count),
          borderColor: lineColors.sensemakers,
          backgroundColor: sensemakersAreaFill,
          showLine: true,
          fill: 'origin',
          tension: 0,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: lineColors.sensemakers,
          pointBorderColor: '#fff',
          pointBorderWidth: 1.5,
          borderWidth: 2,
          borderCapStyle: 'butt',
          borderJoinStyle: 'miter',
        },
        {
          type: 'line' as const,
          label: 'Signal Providers',
          data: trend.signalProviders.map((b) => b.count),
          borderColor: lineColors.signalProviders,
          backgroundColor: 'transparent',
          showLine: true,
          fill: false,
          tension: 0,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: lineColors.signalProviders,
          pointBorderColor: '#fff',
          pointBorderWidth: 1.5,
          borderWidth: 2,
          borderCapStyle: 'butt',
          borderJoinStyle: 'miter',
        },
      ],
    }),
    [labels, trend],
  )

  const options = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: { top: 4, right: 4, bottom: 0, left: 0 },
      },
      datasets: {
        line: {
          showLine: true,
          fill: false,
          tension: 0,
          borderWidth: 2,
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          mode: 'index',
          intersect: false,
          backgroundColor: '#fff',
          borderWidth: 1.5,
          borderColor: lineColors.sensemakers,
          titleColor: '#000',
          bodyColor: '#000',
          titleFont: { family: 'Inter, sans-serif', size: 12, weight: 'normal' },
          bodyFont: { family: '"Plus Jakarta Sans", Inter, sans-serif', size: 12, weight: 'bold' },
          padding: 10,
          cornerRadius: 8,
          displayColors: true,
          boxWidth: 8,
          boxHeight: 8,
          usePointStyle: true,
          callbacks: {
            title: (items) => {
              const idx = items[0]?.dataIndex ?? 0
              return formatGrowthTrendTooltipTitle(trend.sensemakers[idx], growthPeriod)
            },
            label: (ctx) => {
              const count = ctx.parsed.y ?? 0
              return `${ctx.dataset.label ?? ''}: ${count}`
            },
          },
        },
      },
      interaction: {
        mode: 'index',
        intersect: false,
      },
      elements: {
        line: {
          showLine: true,
          tension: 0,
          borderWidth: 2,
          fill: false,
        },
        point: {
          radius: 4,
          hoverRadius: 6,
          borderWidth: 1.5,
        },
      },
      scales: {
        x: {
          type: 'category',
          offset: false,
          grid: { color: lineColors.grid, drawBorder: false, tickLength: 0 },
          ticks: {
            color: card.tickColor,
            font: tickFont,
            maxRotation: 0,
            padding: 4,
          },
          title: {
            display: true,
            text: growthTrendXAxisTitle(growthPeriod),
            color: card.axisTitleColor,
            font: axisTitleFont,
            padding: { top: 6, bottom: card.xTitleToLegendGap },
          },
        },
        y: {
          beginAtZero: true,
          grid: { color: lineColors.grid, drawBorder: false, tickLength: 0 },
          ticks: {
            color: card.tickColor,
            font: tickFont,
            precision: 0,
            padding: 4,
            maxTicksLimit: 8,
          },
          title: {
            display: true,
            text: card.yAxisTitle,
            color: card.axisTitleColor,
            font: axisTitleFont,
            padding: { bottom: 6 },
          },
        },
      },
    }),
    [growthPeriod, trend],
  )

  return (
    <Box
      data-testid="platform-growth-trend-chart"
      sx={{
        borderRadius: card.borderRadius,
        boxShadow: card.boxShadow,
        px: card.paddingX,
        pt: card.paddingY,
        pb: card.paddingBottom,
        bgcolor: card.bgcolor,
        height: card.height,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography
          sx={{
            fontSize: card.titleFontSize,
            fontWeight: card.titleFontWeight,
            fontFamily: card.titleFontFamily,
            color: card.titleColor,
          }}
        >
          Growth Trend
        </Typography>
        <WeekMonthToggle
          value={growthPeriod}
          onChange={onGrowthPeriodChange}
          testId="platform-growth-trend-toggle"
          variant="chart"
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: card.plotMinHeight,
          position: 'relative',
          opacity: isPeriodRefreshing ? 0.45 : 1,
          transition: 'opacity 0.2s ease',
        }}
        aria-busy={isPeriodRefreshing}
      >
        <Line data={data} options={options} redraw />
      </Box>
      <Box
        data-testid="platform-growth-trend-legend"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 3,
          pt: 2,
          pb: 0.5,
        }}
      >
        {GROWTH_TREND_LEGEND.map((item) => (
          <Box
            key={item.key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: ekycLegend.legendItemGap,
              minHeight: ekycLegend.legendRowHeight,
            }}
          >
            <Box
              aria-hidden
              sx={{
                width: ekycLegend.legendSwatchWidth,
                height: ekycLegend.legendSwatchHeight,
                borderRadius: ekycLegend.legendSwatchRadius,
                bgcolor: item.color,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: '12px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                lineHeight: ekycLegend.legendLineHeight,
                color: ekycLegend.legendLabel,
                whiteSpace: 'nowrap',
              }}
            >
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )
}
